import React, { forwardRef, useImperativeHandle } from 'react'
import { PDFDownloadLink, PDFViewer, pdf } from '@react-pdf/renderer'
import { Button } from '@mui/material'
import { Download as DownloadIcon } from '@mui/icons-material'
import OpCaseSheetPDF from './OpCaseSheetPrintPDF'
import { Patient } from 'fhir/r4'
import { upComingAppointmentType } from './patientsDashboard'
import { useAppSelector } from '../../../app/hooks'
import { selectLocations } from '../../location/locationSlice'
import { getLocationAddress } from '../utils'
import { toast } from 'react-toastify'
import {
  opCaseSheetPDFViewer,
  viewer,
  pdfViewerLabels,
  thopIdentifier,
} from '../constants'
import { patientConstants } from '../../lms/constants'

type PatientProps = {
  patient: Patient
  historyList: any
  examinationList: any
  vitalsList: any
  diagnosisList: any
  labOrderList: any
  radiologyList: any
  visitsList: any
  upcomingAppointments?: upComingAppointmentType
  viewMode?: 'download' | 'viewer' // Add mode to control rendering
}

export interface OpCaseSheetPDFRef {
  downloadPDF: () => Promise<void>
}

const OpCaseSheetPDFViewer = forwardRef<OpCaseSheetPDFRef, PatientProps>(
  (
    {
      patient,
      historyList,
      examinationList,
      vitalsList,
      diagnosisList,
      labOrderList,
      radiologyList,
      visitsList,
      upcomingAppointments,
      viewMode = 'download',
    },
    ref
  ) => {
    const getRegistrationNo = (patient: Patient | undefined) => {
      const identifier = patient?.identifier
      if (identifier) {
        const regNo = identifier.find((data: any) =>
          data?.system?.includes(patientConstants?.patientIdentifier)
            ? data
            : ''
        )
        const thopId = identifier.find((data: any) =>
          data?.system?.includes(thopIdentifier) ? data : ''
        )
        return { regNo, thopId }
      }
      return ''
    }

    const registrationNoData: any = getRegistrationNo(patient)
    const registrationNo: any = registrationNoData?.regNo?.value
      ? registrationNoData?.regNo?.value
      : registrationNoData?.thopId?.value

    const locations = useAppSelector(selectLocations)
    const locationAddress = getLocationAddress(registrationNo, locations)

    const patientName = `${patient?.name?.[0]?.given?.[0] || ''} ${
      patient?.name?.[0]?.given?.[1] || ''
    } ${patient?.name?.[0]?.family || ''}`.trim()

    const fileName = `${pdfViewerLabels.opCaseSheetPrefix}${patientName}_${
      registrationNo || pdfViewerLabels.patient
    }${pdfViewerLabels.pdfExtension}`

    // Expose downloadPDF method via ref
    useImperativeHandle(
      ref,
      () => ({
        downloadPDF: async () => {
          try {
            const blob = await pdf(
              <OpCaseSheetPDF
                patient={patient}
                visitsList={visitsList}
                historyList={historyList}
                examinationList={examinationList}
                vitalsList={vitalsList}
                diagnosisList={diagnosisList}
                labOrderList={labOrderList}
                radiologyList={radiologyList}
                upcomingAppointments={upcomingAppointments}
                locationAddress={locationAddress}
              />
            ).toBlob()
            const url = URL.createObjectURL(blob)
            const newWindow = window.open(url, '_blank')
            if (newWindow) {
              const checkClosed = setInterval(() => {
                if (newWindow.closed) {
                  URL.revokeObjectURL(url)
                  clearInterval(checkClosed)
                }
              }, 1000)
              setTimeout(() => {
                URL.revokeObjectURL(url)
                clearInterval(checkClosed)
              }, 300000) // 5 minutes
            }
          } catch (error) {
            toast.error(`${pdfViewerLabels.errorGeneratingPDF}: ${error}`)
          }
        },
      }),
      [
        patient,
        visitsList,
        historyList,
        examinationList,
        vitalsList,
        diagnosisList,
        labOrderList,
        radiologyList,
        upcomingAppointments,
        locationAddress,
      ]
    )
    if (viewMode === viewer) {
      return (
        <PDFViewer className='pdf-viewer'>
          <OpCaseSheetPDF
            patient={patient}
            visitsList={visitsList}
            historyList={historyList}
            examinationList={examinationList}
            vitalsList={vitalsList}
            diagnosisList={diagnosisList}
            labOrderList={labOrderList}
            radiologyList={radiologyList}
            upcomingAppointments={upcomingAppointments}
            locationAddress={locationAddress}
          />
        </PDFViewer>
      )
    }
    return (
      <div>
        <PDFDownloadLink
          document={
            <OpCaseSheetPDF
              patient={patient}
              visitsList={visitsList}
              historyList={historyList}
              examinationList={examinationList}
              vitalsList={vitalsList}
              diagnosisList={diagnosisList}
              labOrderList={labOrderList}
              radiologyList={radiologyList}
              upcomingAppointments={upcomingAppointments}
              locationAddress={locationAddress}
            />
          }
          fileName={fileName}
          className='opCaseSheetPdf-download'
        >
          <Button variant='contained' startIcon={<DownloadIcon />}>
            {pdfViewerLabels.downloadOpCaseSheet}
          </Button>
        </PDFDownloadLink>
      </div>
    )
  }
)

OpCaseSheetPDFViewer.displayName = opCaseSheetPDFViewer

export default OpCaseSheetPDFViewer
